import { BRAND_SUFFIX, DEFAULT_OG_IMAGE, SITE_URL } from './constants';
import {
    buildJsonLdGraph,
    courseSchema,
    faqPageSchema,
    organizationSchema,
    webSiteSchema,
    webPageSchema,
    breadcrumbListSchema,
    personSchema,
    itemListSchema,
    bookSchema,
    imageObjectSchema,
    absoluteUrl,
} from './json-ld';
import { grade10Faq } from '@/components/welcome/landing/grade10/grade-10-data';
import { grade11Faq } from '@/components/welcome/landing/grade11/grade-11-data';
import {
    grade11CurriculumConfig,
    curriculumFaqItems,
    curriculumMap as grade11CurriculumMap,
    curriculumChapters as grade11CurriculumChapters,
} from '@/components/welcome/landing/grade11-curriculum/grade11-curriculum-data';
import {
    grade10CurriculumConfig,
    curriculumFaqItems as grade10CurriculumFaqItems,
    curriculumUnits as grade10CurriculumUnits,
    curriculumDistributionMaps as grade10CurriculumMaps,
} from '@/components/welcome/landing/grade10-curriculum/grade10-curriculum-data';
import essayPageData from '@/components/welcome/landing/essay-questions/essay-page-data.cjs';

const { essayPageConfig, faqGroups } = essayPageData;
const essayFaqItems = faqGroups
    .reduce((items, group) => items.concat(group.items), [])
    .map(([q, a]) => ({ q, a }));
const essaySchemas = [
    {
        '@type': 'Article',
        headline: essayPageConfig.title,
        description: essayPageConfig.description,
        url: essayPageConfig.canonicalUrl,
        mainEntityOfPage: essayPageConfig.canonicalUrl,
        inLanguage: ['ar', 'en'],
        datePublished: essayPageConfig.datePublished,
        dateModified: essayPageConfig.dateModified,
        author: { '@type': 'Person', name: essayPageConfig.authorName, url: absoluteUrl(essayPageConfig.authorUrl) },
        publisher: { '@type': 'Organization', name: 'Coding With Ghonaim', logo: { '@type': 'ImageObject', url: absoluteUrl(essayPageConfig.logoUrl) } },
        image: absoluteUrl(essayPageConfig.mindmapImage),
    },
    breadcrumbListSchema([
        { name: 'الرئيسية', url: '/' },
        { name: 'حل أسئلة المقالي', url: essayPageConfig.routePath },
    ]),
    faqPageSchema(essayFaqItems),
    imageObjectSchema({
        name: 'إزاي تحل السؤال المقالي؟ How to Solve an Essay Question',
        description: essayPageConfig.mindmapAlt,
        contentUrl: essayPageConfig.mindmapImage,
        width: 1600,
        height: 1200,
        caption: 'طريقة حل السؤال المقالي: اقرأ، حدّد، افتكر، رتّب، اكتب، راجع.',
        inLanguage: 'ar',
        pageUrl: essayPageConfig.routePath,
        pageName: essayPageConfig.title,
    }),
    organizationSchema(),
    personSchema({ name: 'محمد غنيم', alternateName: 'Mohamed Ghonaim', url: '/about' }),
];

if (essayPageConfig.youtubeUrl && essayPageConfig.youtubeEmbedUrl && essayPageConfig.videoUploadDate) {
    essaySchemas.push({
        '@type': 'VideoObject',
        name: 'إزاي تتخلص من خوف أسئلة المقالي؟',
        description: essayPageConfig.description,
        thumbnailUrl: [essayPageConfig.youtubeThumbnail],
        uploadDate: essayPageConfig.videoUploadDate,
        embedUrl: essayPageConfig.youtubeEmbedUrl,
        contentUrl: essayPageConfig.youtubeUrl,
        ...(essayPageConfig.videoDuration ? { duration: essayPageConfig.videoDuration } : {}),
    });
}

const ESSAY_QUESTIONS_SEO = {
    title: essayPageConfig.title,
    description: essayPageConfig.description,
    canonical: essayPageConfig.canonicalUrl,
    robots: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    ogImage: absoluteUrl(essayPageConfig.socialImageUrl || essayPageConfig.mindmapImage),
    ogImageAlt: essayPageConfig.mindmapAlt,
    ogImageWidth: 1200,
    ogImageHeight: 630,
    ogType: 'article',
    jsonLd: buildJsonLdGraph(essaySchemas),
};

const PUBLIC_ROUTE_NAMES = new Set([
    'welcome',
    'grade-10',
    'grade-11',
    'grade-10-programming-ai-curriculum',
    'grade-11-programming-ai-curriculum',
    'essay-questions',
    'courses',
    'course',
    'about',
    'privacy-policy',
    'terms',
    'links',
    'help',
]);

const GRADE10_CURRICULUM_SEO = {
    title: 'منهج البرمجة أولى ثانوي 2025/2026 | الكتاب الرسمي عربي ولغات',
    description:
        'شرح كامل لمنهج البرمجة والذكاء الاصطناعي أولى ثانوي، خرائط توزيع الترم الأول والثاني أسبوع بأسبوع، الدروس المقررة وغير المدرجة، وتحميل كتاب الوزارة عربي ولغات PDF.',
    canonical: grade10CurriculumConfig.canonicalUrl,
    robots: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    ogImage: grade10CurriculumConfig.socialImageUrl,
    ogType: 'website',
    jsonLd: buildJsonLdGraph([
        webPageSchema({
            name: 'منهج البرمجة والذكاء الاصطناعي أولى ثانوي',
            description:
                'شرح كامل لمنهج البرمجة والذكاء الاصطناعي أولى ثانوي، خرائط توزيع الترم الأول والثاني أسبوع بأسبوع، الدروس المقررة وغير المدرجة، وتحميل كتاب الوزارة عربي ولغات PDF.',
            url: grade10CurriculumConfig.canonicalUrl,
            about: [
                'Programming',
                'Artificial Intelligence',
                'Grade 10',
                'Information Technology',
                'Cybersecurity',
                'Networks',
                'Databases',
                'JavaScript',
                'HTML',
                'CSS',
                'Generative AI',
            ],
            datePublished: '2026-08-04',
            dateModified: grade10CurriculumConfig.lastReviewedDate,
        }),
        breadcrumbListSchema([
            { name: 'الرئيسية', url: '/' },
            { name: 'أولى ثانوي', url: '/grade-10' },
            { name: 'منهج البرمجة والذكاء الاصطناعي', url: grade10CurriculumConfig.routePath },
        ]),
        faqPageSchema(grade10CurriculumFaqItems),
        itemListSchema({
            name: 'وحدات منهج البرمجة والذكاء الاصطناعي أولى ثانوي',
            items: grade10CurriculumUnits.map((unit) => ({
                name: `${unit.number}. ${unit.arabicTitle}`,
                url: `${grade10CurriculumConfig.routePath}#${unit.id}`,
                description: unit.summary,
            })),
        }),
        ...grade10CurriculumMaps.map((map) =>
            imageObjectSchema({
                name: map.label,
                description: map.alt,
                contentUrl: map.src,
                width: map.width,
                height: map.height,
                caption: map.caption,
                inLanguage: 'ar',
                pageUrl: grade10CurriculumConfig.routePath,
                pageName: 'منهج البرمجة والذكاء الاصطناعي أولى ثانوي',
            })
        ),
        bookSchema({
            name: 'كتاب البرمجة والذكاء الاصطناعي للصف الأول الثانوي',
            url: grade10CurriculumConfig.arabicBookUrl,
            inLanguage: 'ar',
            educationalLevel: 'First Year Secondary',
            description: `الكتاب الرسمي عربي — العام الدراسي ${grade10CurriculumConfig.officialAcademicYear}`,
        }),
        bookSchema({
            name: 'Programming and Artificial Intelligence — First Year Secondary',
            url: grade10CurriculumConfig.englishBookUrl,
            inLanguage: 'en',
            educationalLevel: 'First Year Secondary',
            description: `Official English book — academic year ${grade10CurriculumConfig.officialAcademicYear}`,
        }),
        organizationSchema(),
        personSchema({
            name: 'محمد غنيم',
            alternateName: 'Mohamed Ghonaim',
            url: '/about',
        }),
    ]),
};

const GRADE11_CURRICULUM_SEO = {
    title: 'منهج البرمجة تانية ثانوي بكالوريا 2026/2027 | كتاب الوزارة عربي ولغات',
    description:
        'تحميل كتاب البرمجة الرسمي للوزارة تانية ثانوي بكالوريا 2026/2027 عربي ولغات، الترم الأول والثاني PDF، مع خريطة الفصول السبعة والكورس التمهيدي مجانًا.',
    canonical: grade11CurriculumConfig.canonicalUrl,
    robots: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    ogImage: grade11CurriculumConfig.socialImageUrl,
    ogType: 'website',
    jsonLd: buildJsonLdGraph([
        webPageSchema({
            name: 'منهج البرمجة والذكاء الاصطناعي تانية ثانوي بكالوريا 2026/2027',
            description:
                'تحميل كتاب البرمجة الرسمي للوزارة تانية ثانوي بكالوريا 2026/2027 عربي ولغات، الترم الأول والثاني PDF، مع خريطة الفصول السبعة والكورس التمهيدي مجانًا.',
            url: grade11CurriculumConfig.canonicalUrl,
            about: [
                'Programming',
                'Artificial Intelligence',
                'Grade 11',
                'Egyptian Baccalaureate',
                'Information Technology',
                'Cybersecurity',
                'Web Applications',
                'Data Analysis',
                'Machine Learning',
                'Generative AI',
            ],
            datePublished: '2026-08-04',
            dateModified: grade11CurriculumConfig.lastReviewedDate,
        }),
        breadcrumbListSchema([
            { name: 'الرئيسية', url: '/' },
            { name: 'تانية ثانوي', url: '/grade-11' },
            { name: 'منهج البرمجة والذكاء الاصطناعي', url: grade11CurriculumConfig.routePath },
        ]),
        faqPageSchema(curriculumFaqItems),
        itemListSchema({
            name: 'فصول منهج البرمجة والذكاء الاصطناعي تانية ثانوي بكالوريا',
            items: grade11CurriculumChapters.map((chapter) => ({
                name: `${chapter.number}. ${chapter.arabicTitle}`,
                url: `${grade11CurriculumConfig.routePath}#${chapter.id}`,
                description: chapter.coreIdea || chapter.englishTitle,
            })),
        }),
        imageObjectSchema({
            name: grade11CurriculumMap.label,
            description: grade11CurriculumMap.alt,
            contentUrl: grade11CurriculumMap.src,
            width: grade11CurriculumMap.width,
            height: grade11CurriculumMap.height,
            caption: grade11CurriculumMap.caption,
            inLanguage: 'ar',
            pageUrl: grade11CurriculumConfig.routePath,
            pageName: 'منهج البرمجة والذكاء الاصطناعي تانية ثانوي بكالوريا 2026/2027',
        }),
        courseSchema({
            name: 'كورس تمهيدي مجاني — أساسيات أولى ثانوي قبل تانية ثانوي',
            description:
                'كورس تمهيدي مجاني لمراجعة أهم أساسيات Programming & AI في أولى ثانوي قبل بدء منهج تانية ثانوي بكالوريا.',
            url: `${SITE_URL}${grade11CurriculumConfig.introCourseUrl}`,
            image: grade11CurriculumConfig.socialImageUrl,
        }),
        courseSchema({
            name: 'كورس الشهر الأول — منهج البرمجة تانية ثانوي بكالوريا',
            description:
                'كورس الشهر الأول لمنهج البرمجة والذكاء الاصطناعي تانية ثانوي بكالوريا 2026/2027 مع شرح نظري وعملي وتدريبات.',
            url: `${SITE_URL}${grade11CurriculumConfig.monthOneCourseUrl}`,
            image: grade11CurriculumConfig.socialImageUrl,
        }),
        bookSchema({
            name: 'كتاب البرمجة والذكاء الاصطناعي للصف الثاني الثانوي — عربي الترم الأول',
            url: grade11CurriculumConfig.arabicTerm1BookUrl,
            inLanguage: 'ar',
            educationalLevel: 'Second Year Secondary',
            description: `الكتاب الرسمي عربي — الترم الأول — العام الدراسي ${grade11CurriculumConfig.academicYear}`,
        }),
        bookSchema({
            name: 'كتاب البرمجة والذكاء الاصطناعي للصف الثاني الثانوي — عربي الترم الثاني',
            url: grade11CurriculumConfig.arabicTerm2BookUrl,
            inLanguage: 'ar',
            educationalLevel: 'Second Year Secondary',
            description: `الكتاب الرسمي عربي — الترم الثاني — العام الدراسي ${grade11CurriculumConfig.academicYear}`,
        }),
        bookSchema({
            name: 'Programming and Artificial Intelligence — Grade 11 English Book Term 1',
            url: grade11CurriculumConfig.englishTerm1BookUrl,
            inLanguage: 'en',
            educationalLevel: 'Second Year Secondary',
            description: `Official English ministry book — Term 1 — academic year ${grade11CurriculumConfig.academicYear}`,
        }),
        bookSchema({
            name: 'Programming and Artificial Intelligence — Grade 11 English Book Term 2',
            url: grade11CurriculumConfig.englishTerm2BookUrl,
            inLanguage: 'en',
            educationalLevel: 'Second Year Secondary',
            description: `Official English ministry book — Term 2 — academic year ${grade11CurriculumConfig.academicYear}`,
        }),
        organizationSchema(),
        personSchema({
            name: 'محمد غنيم',
            alternateName: 'Mohamed Ghonaim',
            url: '/about',
        }),
    ]),
};

const STATIC_ROUTE_SEO = {
    welcome: {
        title: `البرمجة مع غنيم | تعلم Programming & AI لأولى وتانية ثانوي`,
        description:
            'ابدأ تعلم البرمجة والذكاء الاصطناعي مع البرمجة مع غنيم. كورسات منظمة لطلاب أولى وتانية ثانوي مع شرح، تدريب، Coding Lab، مراجعات، ومتابعة.',
        canonical: `${SITE_URL}/`,
        jsonLd: buildJsonLdGraph([organizationSchema(), webSiteSchema()]),
    },
    'grade-10': {
        title: `كورس أولى ثانوي Programming & AI${BRAND_SUFFIX}`,
        description:
            'كورس أولى ثانوي Programming & AI مع البرمجة مع غنيم. تأسيس من البداية، شرح بسيط، تدريب عملي، مراجعات، ومناسب لطلاب العربي واللغات وEnglish Schools.',
        canonical: `${SITE_URL}/grade-10`,
        jsonLd: buildJsonLdGraph([faqPageSchema(grade10Faq.items)]),
    },
    'grade-10-programming-ai-curriculum': GRADE10_CURRICULUM_SEO,
    'grade-11': {
        title: `كورس تانية ثانوي Programming & AI${BRAND_SUFFIX}`,
        description:
            'كورس تانية ثانوي Programming & AI مع البرمجة مع غنيم. نظام كامل للمنهج، شرح، تدريب عملي، Coding Lab، Community، Calendar، مراجعات وامتحانات.',
        canonical: `${SITE_URL}/grade-11`,
        jsonLd: buildJsonLdGraph([faqPageSchema(grade11Faq.items)]),
    },
    'grade-11-programming-ai-curriculum': GRADE11_CURRICULUM_SEO,
    'essay-questions': ESSAY_QUESTIONS_SEO,
    courses: {
        title: `كورسات البرمجة والذكاء الاصطناعي${BRAND_SUFFIX}`,
        description:
            'اكتشف كورسات البرمجة مع غنيم لطلاب أولى وتانية ثانوي، مع برامج إضافية تساعد الطالب يفهم التكنولوجيا والذكاء الاصطناعي ويستعد للمستقبل.',
        canonical: `${SITE_URL}/courses`,
    },
    about: {
        title: `من هو محمد غنيم؟${BRAND_SUFFIX}`,
        description:
            'تعرف على محمد غنيم، مؤسس البرمجة مع غنيم، وخبرته في التعليم، التكنولوجيا، الذكاء الاصطناعي، وبناء المنصات التعليمية.',
        canonical: `${SITE_URL}/about`,
    },
    'privacy-policy': {
        title: `سياسة الخصوصية${BRAND_SUFFIX}`,
        description:
            'تعرف على سياسة الخصوصية في البرمجة مع غنيم وكيفية التعامل مع بيانات الطلاب وأولياء الأمور داخل المنصة.',
        canonical: `${SITE_URL}/privacy-policy`,
    },
    terms: {
        title: `شروط الاستخدام${BRAND_SUFFIX}`,
        description:
            'اقرأ شروط استخدام منصة البرمجة مع غنيم، قواعد الاشتراك، استخدام المحتوى، وحقوق ومسؤوليات الطلاب وأولياء الأمور.',
        canonical: `${SITE_URL}/terms-and-conditions`,
    },
    links: {
        title: `روابطنا${BRAND_SUFFIX}`,
        description: 'روابط التواصل الرسمية لمنصة البرمجة مع غنيم على واتساب، يوتيوب، تيليجرام، والمنصة.',
        canonical: `${SITE_URL}/links`,
        robots: 'index,follow',
    },
    help: {
        title: `الدعم الفني${BRAND_SUFFIX}`,
        description: 'تواصل مع فريق الدعم الفني في البرمجة مع غنيم للمساعدة في الحساب، الاشتراك، والمحتوى.',
        canonical: `${SITE_URL}/help`,
        robots: 'index,follow',
    },
    login: {
        title: `تسجيل الدخول${BRAND_SUFFIX}`,
        description: 'سجّل دخولك إلى منصة البرمجة مع غنيم لمتابعة كورساتك ومحاضراتك.',
        robots: 'noindex,nofollow',
    },
    register: {
        title: `إنشاء حساب جديد${BRAND_SUFFIX}`,
        description: 'أنشئ حساباً على منصة البرمجة مع غنيم وابدأ تعلم البرمجة والذكاء الاصطناعي.',
        robots: 'noindex,nofollow',
    },
    'reset-password': {
        title: `استعادة كلمة المرور${BRAND_SUFFIX}`,
        description: 'استعد كلمة مرور حسابك في البرمجة مع غنيم.',
        robots: 'noindex,nofollow',
    },
    'not-found': {
        title: `الصفحة غير موجودة${BRAND_SUFFIX}`,
        description: 'الصفحة التي تبحث عنها غير موجودة على منصة البرمجة مع غنيم.',
        robots: 'noindex,nofollow',
    },
};

export function isPublicRoute(route) {
    return PUBLIC_ROUTE_NAMES.has(route.name);
}

export function getStaticRouteSeo(route) {
    if (!route || !route.name) {
        return null;
    }

    if (route.name === 'course') {
        return null;
    }

    const staticConfig = STATIC_ROUTE_SEO[route.name];
    if (staticConfig) {
        return {
            ogImage: DEFAULT_OG_IMAGE,
            robots: staticConfig.robots || 'index,follow',
            ...staticConfig,
        };
    }

    if (route.meta && route.meta.title) {
        const path = route.path.endsWith('/') && route.path.length > 1
            ? route.path.slice(0, -1)
            : route.path;
        return {
            title: `${route.meta.title}${BRAND_SUFFIX}`,
            description: `${route.meta.title} — ${SITE_URL.replace('https://', '')}`,
            canonical: absoluteUrl(path),
            ogImage: DEFAULT_OG_IMAGE,
            robots: isPublicRoute(route) ? 'index,follow' : 'noindex,nofollow',
        };
    }

    return null;
}

export function buildCoursePageSeo(course) {
    const courseName = course.name || 'كورس';
    const description = `تعرف على ${courseName} من البرمجة مع غنيم، محتوى منظم يساعد الطالب يفهم، يطبق، ويتدرب خطوة بخطوة.`;
    const canonical = `${SITE_URL}/courses/${course.id}`;
    const image = course.image_url && /^https?:\/\//i.test(course.image_url)
        ? course.image_url
        : DEFAULT_OG_IMAGE;

    return {
        title: `${courseName}${BRAND_SUFFIX}`,
        description,
        canonical,
        ogImage: image,
        robots: 'index,follow',
        jsonLd: courseSchema({
            name: courseName,
            description: course.description || description,
            url: canonical,
            image,
        }),
    };
}
